import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Button,
  Select,
  Row,
  Col,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import axiosInstance from "../../utils/axios";
import PageHeading from "../../components/PageHeading";
import { DingtalkCircleFilled } from "@ant-design/icons";

const { Option } = Select;

const SectionModal = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [groupPrices, setGroupPrices] = useState([{ min_quantity: '', group_price: '' }]);
  const [section, setSection] = useState(null);
  const isUpdate = Boolean(id);

  useEffect(() => {
    fetchEvents();
    if (isUpdate) {
      fetchSection();
    }
  }, [id]);

  const fetchEvents = async () => {
    try {
      const response = await axiosInstance.get('news_and_events/events/');
      setEvents(response.data.results || []);
    } catch (error) {
      message.error("Failed to fetch events");
    }
  };

  const fetchSection = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`news_and_events/event-sections/${id}/`);
      const data = response.data;
      setSection(data);
      form.setFieldsValue({
        title: data.title,
        price: data.price,
        count: data.count,
        released: data.released,
        reserved: data.reserved,
        event: data.event?.id
      });
      setGroupPrices(data.group_prices || [{ min_quantity: '', group_price: '' }]);
    } catch (error) {
      message.error("Failed to fetch section");
    } finally {
      setLoading(false);
    }
  };

  const addGroupPriceRow = () => {
    setGroupPrices([...groupPrices, { min_quantity: '', group_price: '' }]);
  };

  const removeGroupPriceRow = (index) => {
    if (groupPrices.length > 1) {
      setGroupPrices(groupPrices.filter((_, i) => i !== index));
    }
  };

  const updateGroupPrice = (index, field, value) => {
    const updated = groupPrices.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    );
    setGroupPrices(updated);
  };

  const handleFinish = async (values) => {
    setLoading(true);
    const payload = {
      title: values.title,
      price: parseFloat(values.price),
      count: parseInt(values.count),
      released: parseInt(values.released),
      reserved: parseInt(values.reserved),
      event: values.event,
      group_prices: groupPrices
        .filter(row => row.min_quantity && row.group_price)
        .map(row => ({
          min_quantity: parseInt(row.min_quantity),
          group_price: parseFloat(row.group_price)
        }))
    };

    try {
      if (isUpdate) {
        await axiosInstance.patch(`news_and_events/event-sections/${id}/`, payload);
        message.success("Section updated successfully!");
      } else {
        // For create, redirect to list (no create from here)
        message.warning("Create not implemented from this page");
        return;
      }
      navigate('/admin/event-sections');
    } catch (error) {
      message.error("Failed to save section");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/event-sections');
  };

  return (
    <div className="dashboard">
      <PageHeading 
        name={isUpdate ? "Edit Event Section" : "Event Section"} 
        icon={<DingtalkCircleFilled />} 
      />
      
      <Button onClick={handleBack} style={{ marginBottom: 16 }}>
        ← Back to Sections
      </Button>

      <Form
        form={form}
        layout="vertical"
        name="section_form"
        onFinish={handleFinish}
        initialValues={{ released: 0, reserved: 0 }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Event *"
              name="event"
              rules={[{ required: true, message: "Please select event" }]}
            >
              <Select placeholder="Select event" showSearch>
                {events.map(event => (
                  <Option key={event.id} value={event.id}>
                    {event.heading} ({event.venue})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Title *"
              name="title"
              rules={[{ required: true, message: "Please enter title" }]}
            >
              <Input placeholder="e.g., VIP Section" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Base Price ($)" name="price" rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Total Count" name="count" rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Released" name="released" rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Reserved" name="reserved" rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
        </Row>

        <div style={{ marginBottom: 24 }}>
          <h4>Group Prices <small>(Optional - appears below base price for bulk discounts)</small></h4>
          {groupPrices.map((row, index) => (
            <div key={index} style={{ 
              border: '1px solid #d9d9d9', 
              padding: 16, 
              marginBottom: 12, 
              borderRadius: 6,
              background: '#fafafa'
            }}>
              <Row gutter={16} align="middle">
                <Col span={8}>
                  <label>Min Qty</label>
                  <InputNumber
                    value={row.min_quantity}
                    onChange={(val) => updateGroupPrice(index, 'min_quantity', val)}
                    min={2}
                    style={{ width: '100%' }}
                    placeholder="e.g., 5"
                  />
                </Col>
                <Col span={8}>
                  <label>Group Price ($)</label>
                  <InputNumber
                    value={row.group_price}
                    onChange={(val) => updateGroupPrice(index, 'group_price', val)}
                    min={0}
                    step={0.01}
                    style={{ width: '100%' }}
                    placeholder="e.g., 27.99"
                  />
                </Col>
                <Col span={8}>
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeGroupPriceRow(index)}
                    disabled={groupPrices.length === 1}
                    style={{ padding: '12px 0' }}
                  />
                </Col>
              </Row>
            </div>
          ))}
          <Button 
            type="dashed" 
            icon={<PlusOutlined />} 
            onClick={addGroupPriceRow} 
            block
            style={{ borderStyle: 'dashed' }}
          >
            Add Group Price Row
          </Button>
        </div>

        <Row>
          <Col>
            <Button type="primary" htmlType="submit" loading={loading} size="large">
              {isUpdate ? 'Update Section' : 'Create Section'}
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default SectionModal;

